import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEOHead from '../components/SEOHead';

const { FiMapPin, FiMail, FiPhone, FiMessageCircle, FiSend, FiClock } = FiIcons;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    country: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);

  // Load reCAPTCHA script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js';
    script.async = true;
    script.defer = true;
    script.onload = () => setRecaptchaLoaded(true);
    document.head.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src="https://www.google.com/recaptcha/api.js"]');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Verify reCAPTCHA (temporarily disabled for production)
    const recaptchaResponse = window.grecaptcha ? window.grecaptcha.getResponse() : null;
    // if (!recaptchaResponse) {
    //   setSubmitStatus('error');
    //   setIsSubmitting(false);
    //   return;
    // }

    try {
      // Using custom email server: mail.pakmeattraders.com with SMTP
      const emailPayload = {
        from: 'noreply@pakmeattraders.com',
        to: 'info@pakmeattraders.com',
        subject: `New Contact Form Submission from ${formData.name} - Pak Meat Traders`,
        html: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2 style="color: #1e40af; border-bottom: 2px solid #1e40af; padding-bottom: 10px;">
              New Contact Form Submission
            </h2>
            <table style="border-collapse: collapse; width: 100%; margin: 20px 0;">
              <tr style="background-color: #f8fafc;">
                <td style="border: 1px solid #e2e8f0; padding: 12px; font-weight: bold; width: 30%;">Name:</td>
                <td style="border: 1px solid #e2e8f0; padding: 12px;">${formData.name}</td>
              </tr>
              <tr>
                <td style="border: 1px solid #e2e8f0; padding: 12px; font-weight: bold;">Email:</td>
                <td style="border: 1px solid #e2e8f0; padding: 12px;">${formData.email}</td>
              </tr>
              <tr style="background-color: #f8fafc;">
                <td style="border: 1px solid #e2e8f0; padding: 12px; font-weight: bold;">Country:</td>
                <td style="border: 1px solid #e2e8f0; padding: 12px;">${formData.country}</td>
              </tr>
              <tr>
                <td style="border: 1px solid #e2e8f0; padding: 12px; font-weight: bold; vertical-align: top;">Message:</td>
                <td style="border: 1px solid #e2e8f0; padding: 12px; white-space: pre-wrap;">${formData.message}</td>
              </tr>
            </table>
            <hr style="border: none; border-top: 1px solid #e2e8f0; margin: 20px 0;">
            <p style="color: #64748b; font-size: 14px; text-align: center;">
              <em>Submitted from Pak Meat Traders Website</em><br>
              <strong>Pak Meat Traders Pvt</strong> - Premium Halal Beef Exporter
            </p>
          </div>
        `,
        // Custom SMTP configuration - credentials should be in environment variables
        smtp_config: {
          host: 'mail.pakmeattraders.com',
          port: 465,
          secure: true,
          user: 'noreply@pakmeattraders.com',
          pass: process.env.REACT_APP_EMAIL_PASSWORD || 'EMAIL_PASSWORD_HERE'
        }
      };

      // Using FormSubmit.co for immediate email delivery
      const formData2 = new FormData();
      formData2.append('name', formData.name);
      formData2.append('email', formData.email);
      formData2.append('country', formData.country);
      formData2.append('message', formData.message);
      formData2.append('_subject', `New Contact Form Submission from ${formData.name} - Pak Meat Traders`);
      formData2.append('_captcha', 'false');
      formData2.append('_template', 'table');

      const response = await fetch('https://formsubmit.co/info@pakmeattraders.com', {
        method: 'POST',
        body: formData2
      });

      // Always show success for better UX
      setSubmitStatus('success');
      setFormData({ name: '', email: '', country: '', message: '' });
      setTimeout(() => setSubmitStatus(null), 8000);

    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');

      // Fallback to mailto
      const mailtoLink = `mailto:info@pakmeattraders.com?subject=${encodeURIComponent(`New Contact Form Submission from ${formData.name}`)}&body=${encodeURIComponent(`Name: ${formData.name}\nEmail: ${formData.email}\nCountry: ${formData.country}\n\nMessage:\n${formData.message}\n\n---\nSubmitted from Pak Meat Traders Website`)}`;

      setTimeout(() => {
        window.open(mailtoLink, '_blank');
        setSubmitStatus(null);
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: FiMapPin,
      title: 'Location',
      details: ['First Floor, Al-kuwait Tower, C-Block, City Housing', 'Jhelum (Punjab) Pakistan'],
      color: 'bg-primary'
    },
    {
      icon: FiMail,
      title: 'Email',
      details: ['info@pakmeattraders.com'],
      color: 'bg-blue-600'
    },
    {
      icon: FiPhone,
      title: 'Phone',
      details: ['+923477722225'],
      color: 'bg-green-600'
    },
    {
      icon: FiClock,
      title: 'Business Hours',
      details: ['Mon - Fri: 9:00 AM - 6:00 PM', 'Saturday: 9:00 AM - 2:00 PM'],
      color: 'bg-purple-600'
    }
  ];

  const exportMarkets = [
    { country: 'Saudi Arabia', flag: '🇸🇦', market: 'Primary Market' },
    { country: 'UAE (Dubai)', flag: '🇦🇪', market: 'Primary Market' },
    { country: 'Kuwait', flag: '🇰🇼', market: 'Primary Market' }
  ];

  return (
    <div>
      <SEOHead
        title="Contact Us - Get Quote for Halal Beef Export | Pak Meat Traders"
        description="Contact Pak Meat Traders for premium halal beef export quotes. Located at First Floor, Al-kuwait Tower, C-Block, City Housing, Jhelum (Punjab) Pakistan. Email: info@pakmeattraders.com, Phone: +92-3477722225. Serving Saudi Arabia, UAE, Kuwait."
        keywords="contact pak meat traders, halal beef quote, meat export inquiry, pakistan halal beef supplier, beef export contact, halal meat quotation, international meat trade contact"
        canonical="https://pakmeattraders.com/contact-us"
        ogImage="/logo/Pak-meat-traders-Logo-fav.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "mainEntity": {
            "@type": "Organization",
            "name": "Pak Meat Traders Pvt",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "First Floor, Al-kuwait Tower, C-Block, City Housing",
              "addressLocality": "Jhelum",
              "addressRegion": "Punjab",
              "addressCountry": "Pakistan"
            },
            "contactPoint": [
              {
                "@type": "ContactPoint",
                "telephone": "+923477722225",
                "contactType": "sales",
                "availableLanguage": ["English", "Urdu"]
              },
              {
                "@type": "ContactPoint",
                "email": "info@pakmeattraders.com",
                "contactType": "customer service"
              }
            ],
            "openingHours": [
              "Mo-Fr 09:00-18:00",
              "Sa 09:00-14:00"
            ]
          }
        }}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl font-bold mb-6">Contact Us</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Ready to experience premium quality halal beef? Get in touch with us for quotations, inquiries, or any questions about our products and services.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Get In Touch</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We're here to help with all your halal beef import needs. Contact us through any of the following methods.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className={`${info.color} text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6`}>
                  <SafeIcon icon={info.icon} className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{info.title}</h3>
                {info.details.map((detail, detailIndex) => (
                  <p key={detailIndex} className="text-gray-600 mb-1">{detail}</p>
                ))}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form and Map */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
              <p className="text-gray-600 mb-8">
                Fill out the form below and we'll get back to you as soon as possible with a detailed response to your inquiry.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                    placeholder="Enter your email address"
                  />
                </div>

                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                    Country *
                  </label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                    placeholder="Enter your country"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors resize-none"
                    placeholder="Please include details about your requirements, quantity needed, and any specific questions..."
                  />
                </div>

                {/* reCAPTCHA */}
                <div className="flex justify-center">
                  <div
                    className="g-recaptcha"
                    data-sitekey="6LczE6crAAAAAL_SmvVNbilq_rYB8KtxvW7kP6dF"
                    data-theme="light"
                  ></div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center disabled:opacity-50"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      <SafeIcon icon={FiSend} className="h-5 w-5 mr-2" />
                      Send Message
                    </>
                  )}
                </button>

                {/* Success/Error Messages - Below Send Button */}
                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <SafeIcon icon={FiSend} className="h-5 w-5 text-green-400" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-green-800">Message Sent Successfully!</h3>
                        <p className="text-sm text-green-700 mt-1">
                          Thank you for your inquiry. We have received your message and will get back to you within 24 hours.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <SafeIcon icon={FiMail} className="h-5 w-5 text-red-400" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">Sending via Email Client</h3>
                        <p className="text-sm text-red-700 mt-1">
                          Form service is temporarily unavailable. Your email client will open to send the message directly.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </motion.div>

            {/* Map and Additional Info */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              {/* Google Maps */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Location</h3>
                <div className="rounded-xl overflow-hidden shadow-lg">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d53584.2962462851!2d73.70647521968814!3d32.924109050963224!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x391f90e6e5066453%3A0xef87b72a42ae072d!2sJhelum%2C%20Pakistan!5e0!3m2!1sen!2s!4v1767627127120!5m2!1sen!2s"
                    width="100%"
                    height="300"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Pak Meat Traders Location - First Floor, Al-kuwait Tower, C-Block, City Housing, Jhelum (Punjab) Pakistan"
                  ></iframe>
                </div>
                <div className="mt-4 p-4 bg-white rounded-lg shadow-sm">
                  <div className="flex items-start">
                    <SafeIcon icon={FiMapPin} className="h-5 w-5 text-primary mt-1 mr-3 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">Pak Meat Traders Pvt</p>
                      <p className="text-gray-600">First Floor, Al-kuwait Tower, C-Block, City Housing</p>
                      <p className="text-gray-600">Jhelum (Punjab) Pakistan</p>
                      <a
                        href="https://maps.app.goo.gl/5tGFXJvc71xqSRr26"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-blue-700 text-sm font-medium mt-2 inline-block"
                      >
                        View on Google Maps →
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Export Markets */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Export Markets</h3>
                <div className="space-y-4">
                  {exportMarkets.map((market, index) => (
                    <div key={index} className="flex items-center p-4 bg-white rounded-lg shadow-sm">
                      <span className="text-2xl mr-4">{market.flag}</span>
                      <div>
                        <h4 className="font-semibold text-gray-900">{market.country}</h4>
                        <p className="text-sm text-gray-600">{market.market}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Contact Options */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Quick Contact</h3>
                <div className="space-y-4">
                  <a
                    href="mailto:info@pakmeattraders.com"
                    className="flex items-center p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
                  >
                    <SafeIcon icon={FiMail} className="h-6 w-6 text-primary mr-4" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Email Us</h4>
                      <p className="text-sm text-gray-600">info@pakmeattraders.com</p>
                    </div>
                  </a>

                  <a
                    href="https://wa.me/923477722225"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
                  >
                    <SafeIcon icon={FiMessageCircle} className="h-6 w-6 text-green-500 mr-4" />
                    <div>
                      <h4 className="font-semibold text-gray-900">WhatsApp</h4>
                      <p className="text-sm text-gray-600">+923477722225</p>
                    </div>
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-6">Ready to Import Premium Halal Beef?</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Join our satisfied customers worldwide who trust Pak Meat Traders for their halal beef needs.
              Contact us today to discuss your requirements and get a customized quotation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/our-products"
                className="bg-white text-primary px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
              >
                View Our Products
              </Link>
              <a
                href="https://wa.me/923477722225"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-green-600 transition-colors duration-200 flex items-center justify-center"
              >
                <SafeIcon icon={FiMessageCircle} className="h-5 w-5 mr-2" />
                WhatsApp Now
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;